import Logger from '../util/Logger';
import { popinApi } from './api';
import ApiError from './ApiError';
import { PostMethods } from './postmethods';
import User from '../models/User';
import { RoleType } from '../models/RoleType';
import Tenant from '../models/Tenant';
import {
  NETWORK_CODE_FORBIDDEN,
  NETWORK_CODE_SESSION_CHECK_FAILED,
  NETWORK_CODE_UNAUTHORIZED,
  URL_PARAM_ADMIN_BYPASS
} from '../constants';
import _ from 'lodash';
import { UriUtils } from '../util/UriUtils';
import mixpanel from 'mixpanel-browser';

/**
 * The standard client side checking to make sure the User is able to access the enterprise dashboard
 * @param user
 * @return {*}
 */
const validateEnterpriseDashboardAccess = (user) => {
  let errorMessage = null;
  if (!Tenant.isOrgDashboardEnabled(User.getTenant(user))) {
    errorMessage = `User tenant is not Org Dashboard enabled! ${User.getId(user)}`;
  } else {
    const role = User.getRoleType(user);
    if (!(role === RoleType.ORG_ADMIN || role === RoleType.ORG_OWNER)) {
      errorMessage = `User is not a valid role! ${User.getId(user)} ${role}`;
    } else {
      if (!_.get(user, 'plan.features.enterprise_dashboard')) {
        errorMessage = `Enterprise Dashboard is an Enterprise feature.`;
      }
    }
  }

  if (errorMessage) {
    const adminViewingAsSessionId = getSessionViewingAsAdminId(user);
    if (adminViewingAsSessionId) {
      Logger.beacon(`Enterprise Dashboard session viewing as: ${adminViewingAsSessionId}`);
      errorMessage = null;
    }
  }

  // If null, the user can access the Enterprise Dashboard
  return errorMessage;
};

const getSessionViewingAsAdminId = (user) => {
  if (User.isAdminViewingAs(user)) {
    let adminBypassKey = null;

    // Runs synchronously, so even though this looks weird, it will be set before the callbacks finish.
    UriUtils.forEachSearchParameter((key, value) => {
      if (key === URL_PARAM_ADMIN_BYPASS) {
        adminBypassKey = value;
      }
    });

    // If this is anything - we assume the user is bypassing.
    return adminBypassKey;
  } else {
    return null;
  }
};

const checkUserOrgDashboardAuthorized = (user) => {
  let errorMessage = null;
  if (!User.hasTenant(user)) {
    errorMessage = `User does not have a tenant! ${User.getId(user)}`;
  } else {
    errorMessage = validateEnterpriseDashboardAccess(user);
  }

  // If they have an error message, they are not allowed to access
  if (errorMessage) {
    Logger.error(errorMessage);
    throw ApiError({
      code: NETWORK_CODE_FORBIDDEN,
      errorMessage: 'Sorry, you are not authorized to access the Global Dashboard.'
    }, {
      user: user,
      errorMessage: errorMessage
    });
  } else {
    // Otherwise they are allowed to access and we return the User
    return user;
  }
};

/**
 * Api calls that deal with authentication
 *
 * @type {{authenticate: (function(): *)}}
 */
export const authApi = {

  /**
   * Hits the authenticate endpoint to see if the cookie we store (if any) is valid
   *
   * If the cookie is valid, we will chain onto the /account/settings endpoint to see if we
   * can grab a User object, else we fail and crash and burn and die.
   *
   * @returns {Promise<any>} a User object promise chain or death and despair.
   */
  authenticate: (checkOrgDashboard) => {
    Logger.info(`calling authenticate()`);
    return popinApi.raw(PostMethods.GET, '/authenticate').then(fetchResult => {
      // If this is a 205 error we expect and handle it, else its a real error and we blow up.
      if (fetchResult.status === NETWORK_CODE_SESSION_CHECK_FAILED) {
        throw ApiError({
          url: fetchResult.url,
          code: NETWORK_CODE_UNAUTHORIZED,
          errorMessage: 'Not logged in.'
        });
      } else {
        // Continue
        return null;
      }
    }).then(() => popinApi.get('/account/settings')).then(user => {
      if (checkOrgDashboard) {
        return checkUserOrgDashboardAuthorized(user);
      } else {
        return user;
      }
    });
  },

  settings: () => {
    return popinApi.get('/account/settings');
  },

  verify: (eventSlug) => {
    return popinApi.post('/authenticate/verify', {
      token: 'guest',
      event: eventSlug
    });
  },

  logout: () => {
    Logger.info(`calling logout()`);
    mixpanel.reset();
    return popinApi.post('/authenticate/logout');
  }
};
